'use client';
import { useCallback, useMemo } from 'react';
import { DriverHelper } from './createDriverSlice';
import type { WalkthroughContextType } from './types';
import {
  useWalkthroughManagerStore,
  useWalkthroughManagerStoreOptional,
} from './WalkthroughManagerStoreProvider';

export function useGetWalkthroughManagerStore() {
  const getWalkthroughManagerStore = useWalkthroughManagerStore().getState;
  return { getWalkthroughManagerStore };
}

export const useGetWalkthroughComputeContext = () => {
  const getWalkthroughManagerStore = useWalkthroughManagerStoreOptional()?.getState;

  const getWalkthroughComputeContext = useCallback(() => {
    if (!getWalkthroughManagerStore) {
      return {};
    }
    const state = getWalkthroughManagerStore();
    const context: WalkthroughContextType = {
      activeSession: state.activeSession,
      activeStep: DriverHelper.getActiveStep(getWalkthroughManagerStore),
      activeStepIndex: DriverHelper.getActiveStepIndex(getWalkthroughManagerStore),
      activeWalkthroughId: state.activeWalkthroughId,
      hasNextStep: DriverHelper.hasNextStep(getWalkthroughManagerStore),
      hasPreviousStep: DriverHelper.hasPreviousStep(getWalkthroughManagerStore),
      isFirstStep: DriverHelper.isFirstStep(getWalkthroughManagerStore),
      isLastStep: DriverHelper.isLastStep(getWalkthroughManagerStore),
      steps: DriverHelper.steps(getWalkthroughManagerStore),
    };

    return context;
  }, [getWalkthroughManagerStore]);

  return { getWalkthroughComputeContext };
};

export const useWalkthroughComputeContext = () => {
  const getWalkthroughManagerStore = useWalkthroughManagerStoreOptional()?.getState;
  const activeSession = useWalkthroughManagerStoreOptional()?.hooks.useActiveSession();
  const activeStep = useWalkthroughManagerStoreOptional()?.hooks.useActiveStep();
  const activeStepIndex = useWalkthroughManagerStoreOptional()?.hooks.useActiveStepIndex();
  const activeWalkthroughId = useWalkthroughManagerStoreOptional()?.hooks.useActiveWalkthroughId();
  const hasNextStep = useWalkthroughManagerStoreOptional()?.hooks.useHasNextStep();
  const hasPreviousStep = useWalkthroughManagerStoreOptional()?.hooks.useHasPreviousStep();
  const isFirstStep = useWalkthroughManagerStoreOptional()?.hooks.useIsFirstStep();
  const isLastStep = useWalkthroughManagerStoreOptional()?.hooks.useIsLastStep();
  const steps = useWalkthroughManagerStoreOptional()?.hooks.useSteps();

  const context = useMemo<WalkthroughContextType>(() => {
    // if no getWalkthroughManagerStore then that means we have not wrapped the component with WalkthroughManagerStoreProvider
    // which can be the case for login pages etc
    if (!getWalkthroughManagerStore) {
      return {};
    }
    return {
      activeSession,
      activeStep,
      activeStepIndex,
      activeWalkthroughId,
      hasNextStep,
      hasPreviousStep,
      isFirstStep,
      isLastStep,
      steps,
    };
  }, [
    activeSession,
    activeStep,
    activeStepIndex,
    activeWalkthroughId,
    getWalkthroughManagerStore,
    hasNextStep,
    hasPreviousStep,
    isFirstStep,
    isLastStep,
    steps,
  ]);

  return { walkthroughContext: context };
};
